import React, { useState, useEffect } from "react";
import AuthenticatedAvatar from "../../../components/AuthenticatedAvatar";
import {
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  Form,
  Row,
  Col,
  Pagination,
  Image,
} from "react-bootstrap";
import {
  PlusCircle,
  Film,
  Search,
  CalendarDate,
  ClockHistory,
  PlayCircleFill,
  Trash,
  EyeFill,
  JournalBookmarkFill,
  GeoAlt,
  PersonBadge,
  Hash,
  Tag,
  CalendarEvent,
  Clock,
  InfoCircle,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Paper,
  Chip,
  Divider,
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
} from "@mui/material";
import axios from "axios";
import AddMediaOffcanvas from "./AddMediaOffcanvas";
import { toast, ToastContainer } from "react-toastify";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const getAvailabilityLabel = (cls) => {
  if (cls?.isFullyBooked) return "Fully booked";
  if (cls?.maxParticipants == null) return "Unlimited spots";
  return `${cls?.registrationCount || 0} / ${cls.maxParticipants} spots filled`;
};

const ClassMediaPage = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [classMedia, setClassMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // New state for class details modal
  const [showClassDetailsModal, setShowClassDetailsModal] = useState(false);

  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    status: "",
  });

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        type: filters.type,
        status: filters.status,
      };

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getAllClasses`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        },
      );

      setClasses(res.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        page: res.data.page,
        limit: res.data.limit,
      }));
    } catch (error) {
      toast.error("Failed to fetch class data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClassMedia = async (classId) => {
    setMediaLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/mediaAdmin/getAllMedia/${classId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setClassMedia(res.data.data || []);
      setShowMediaModal(true);
    } catch (error) {
      toast.error("Failed to fetch media data");
    } finally {
      setMediaLoading(false);
    }
  };

  const fetchClassMediaById = async (item) => {
    setMediaLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getClassByIdAdmin/${item?._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setSelectedMedia(res.data.data || []);
        setShowClassDetailsModal(true);
    } catch (error) {
      toast.error("Failed to fetch media data");
    } finally {
      setMediaLoading(false);
    }
  };

  console.log("selectedMedia", selectedMedia);

  const handleAddMedia = (classId) => {
    setSelectedClassId(classId);
    setShowMediaForm(true);
  };

  const handleMediaCountClick = (classId) => {
    setSelectedClassId(classId);
    fetchClassMedia(classId);
  };

  const handleDeleteMedia = async (classId, mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;

    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(
        `${API_BASE_URL}/api/mediaAdmin/deleteMedia/${classId}/${mediaId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setClassMedia((prev) => prev.filter((media) => media._id !== mediaId));

      setClasses((prev) =>
        prev.map((cls) => {
          if (cls._id === classId) {
            return {
              ...cls,
              mediaCount: Math.max(0, (cls.mediaCount || 0) - 1),
            };
          }
          return cls;
        }),
      );

      toast.success("Media deleted successfully");
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchClasses();
  }, [pagination.page, filters, refreshTrigger]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get unique class types for filter dropdown
  const classTypes = [...new Set(classes.map((cls) => cls.Type))].filter(
    Boolean,
  );
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Class Media Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <Film size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Class Media Management
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {pagination.total} total classes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by title..."
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </Box>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              {classTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Blocked">Blocked</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Paper
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading classes...
            </Typography>
          </Box>
        ) : classes.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              No classes found.
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "Title",
                  "Theme",
                  "Type",
                  "Date",
                  "Time",
                  "Media Count",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {classes.map((item, index) => (
                <tr
                  key={item._id}
                  style={{ borderBottom: "1px solid #f0f4ff" }}
                >
                  <td
                    style={{
                      color: "#aab4cc",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {(pagination.page - 1) * pagination.limit + index + 1}
                  </td>
                  <td style={{ fontWeight: 600, color: "#1a237e" }}>
                    {item.title}
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    {item.theme || "N/A"}
                  </td>
                  <td>
                    <Chip
                      label={item.Type || "N/A"}
                      size="small"
                      sx={{
                        backgroundColor: "#e4eaf8",
                        color: "#1a237e",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                    />
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    <CalendarDate size={12} className="me-1" />
                    {item.startDate
                      ? new Date(item.startDate).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    <ClockHistory size={12} className="me-1" />
                    {item?.startTime || "N/A"} <br /> -{item?.endTime || "N/A"}
                  </td>
                  <td>
                    <span
                      className="badge px-3 py-2 rounded-pill"
                      style={{
                        background: "#e4eaf8",
                        color: "#1a237e",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => handleMediaCountClick(item._id)}
                    >
                      <Film size={12} className="me-1" />
                      {item.mediaCount || 0}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge px-3 py-2 rounded-pill ${
                        item.status === "Active" ? "bg-success" : "bg-danger"
                      }`}
                      style={{ fontSize: "0.75rem" }}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <ActionBtn
                        tooltip="View Details"
                        color="#1a237e"
                        onClick={() => fetchClassMediaById(item)}
                      >
                        <EyeFill size={16} />
                      </ActionBtn>
                      <ActionBtn
                        tooltip="Add Media"
                        color="#2e7d32"
                        onClick={() => handleAddMedia(item._id)}
                      >
                        <PlusCircle size={16} />
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(pagination.page - 1) * pagination.limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{pagination.total}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === pagination.page}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === pagination.page
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Paper>

      <AddMediaOffcanvas
        show={showMediaForm}
        handleClose={() => setShowMediaForm(false)}
        classId={selectedClassId}
        onSuccess={() => {
          setRefreshTrigger((prev) => !prev);
          if (showMediaModal) {
            fetchClassMedia(selectedClassId);
          }
        }}
      />

      {/* Class Details Modal */}
      <Modal
        show={showClassDetailsModal}
        onHide={() => setShowClassDetailsModal(false)}
        size="lg"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "1rem" }}
          >
            <JournalBookmarkFill size={20} /> Class Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.8rem" }}>
          {selectedMedia && (
            <div className="row">
              {/* Class Image */}
              {selectedMedia?.Image && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "200px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      mb: 2,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Image
                      src={selectedMedia?.Image}
                      alt={selectedMedia?.title || "Class image"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Grid>
              )}

              {/* Basic Information */}
              <div className="col-12 col-md-6">
                <Card
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e4eaf8",
                    boxShadow: "none",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#1a237e",
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <InfoCircle size={16} /> Basic Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            Title
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 600 }}
                          >
                            {selectedMedia.title}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            Theme
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 500 }}
                          >
                            {selectedMedia.theme || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            Session Type
                          </Typography>
                          <Chip
                            label={selectedMedia.Type || "N/A"}
                            size="small"
                            sx={{
                              backgroundColor: "#e4eaf8",
                              color: "#1a237e",
                              fontSize: "0.75rem",
                              height: "24px",
                            }}
                          />
                        </Box>
                      </Grid> */}

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            Type
                          </Typography>
                          <Chip
                            label={selectedMedia.Type || "N/A"}
                            size="small"
                            sx={{
                              backgroundColor: "#e4eaf8",
                              color: "#1a237e",
                              fontSize: "0.75rem",
                              height: "24px",
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            Availability
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                              variant="body2"
                              sx={{
                                color: selectedMedia.isFullyBooked
                                  ? "#c62828"
                                  : "#1a237e",
                                fontWeight: selectedMedia.isFullyBooked ? 700 : 500,
                              }}
                            >
                              {getAvailabilityLabel(selectedMedia)}
                            </Typography>
                            {selectedMedia.isFullyBooked && (
                              <Chip
                                label="Fully booked"
                                size="small"
                                sx={{
                                  backgroundColor: "#c62828",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                  height: "24px",
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>

                      {/* <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            <CalendarEvent size={12} className="me-1" /> Session
                            Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 500 }}
                          >
                            {formatDate(selectedMedia.sessionDate)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            <CalendarEvent size={12} className="me-1" /> Start
                            Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 500 }}
                          >
                            {formatDate(selectedMedia.startDate)}
                          </Typography>
                        </Box>
                      </Grid> */}

                      {/* <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            <CalendarEvent size={12} className="me-1" /> End
                            Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 500 }}
                          >
                            {formatDate(selectedMedia.endDate)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                          >
                            <Clock size={12} className="me-1" /> Session Time
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1a237e", fontWeight: 500 }}
                          >
                            {selectedMedia.startTime || "N/A"} -{" "}
                            {selectedMedia.endTime || "N/A"}
                          </Typography>
                        </Box>
                      </Grid> */}
                    </Grid>
                  </CardContent>
                </Card>
              </div>

              {/* Location & Instructor */}
              <div className="col-12 col-md-6">
                <Card
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e4eaf8",
                    boxShadow: "none",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#1a237e",
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <GeoAlt size={16} /> Location
                    </Typography>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                      >
                        Address
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#1a237e", fontWeight: 500 }}
                      >
                        {selectedMedia.location?.location || "N/A"}
                      </Typography>
                    </Box>

                    {/* {selectedMedia.location?._id && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#7b8ab8", display: "block", mb: 0.5 }}
                        >
                          Location ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#aab4cc" }}>
                          {selectedMedia.location._id}
                        </Typography>
                      </Box>
                    )} */}

                     <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#1a237e",
                        fontWeight: 700,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PersonBadge size={16} /> Instructor
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <AuthenticatedAvatar
                        sx={{
                          bgcolor: "#1a237e",
                          width: 48,
                          height: 48,
                          fontSize: "1.2rem",
                        }}
                      >
                        {selectedMedia.Instructor?.name?.charAt(0) || "?"}
                      </AuthenticatedAvatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#1a237e", fontWeight: 600 }}
                        >
                          {selectedMedia.Instructor?.name || "N/A"}
                        </Typography>
                        {/* <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                          Instructor ID:{" "}
                          {selectedMedia.Instructor?._id || "N/A"}
                        </Typography> */}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </div>

             
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowClassDetailsModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Media Details Modal */}
      <Modal
        show={showMediaModal}
        onHide={() => setShowMediaModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <Film size={18} /> Class Media Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {mediaLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              gap={2}
              py={5}
            >
              <Spinner
                animation="border"
                style={{ color: "#0d47a1", width: 48, height: 48 }}
              />
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                Loading media details...
              </Typography>
            </Box>
          ) : classMedia.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              gap={2}
              py={5}
            >
              <Film size={48} style={{ color: "#aab4cc" }} />
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                No media found for this class
              </Typography>
            </Box>
          ) : (
            <Table hover className="mb-0 align-middle">
              <thead style={{ background: primaryGradient }}>
                <tr>
                  {[
                    "#",
                    "Title",
                    "Description",
                    "Video",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        padding: "10px 12px",
                        border: "none",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {classMedia.map((media, index) => (
                  <tr
                    key={media._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {media.title}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {media.Description || "N/A"}
                    </td>
                    <td>
                      {media.uploadLink ? (
                        <Button
                          variant="link"
                          href={media.uploadLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-0 d-flex align-items-center"
                          style={{ color: "#0d47a1", textDecoration: "none" }}
                        >
                          <PlayCircleFill size={16} className="me-1" />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 500 }}
                          >
                            Watch Video
                          </Typography>
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                          No video
                        </Typography>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          media.status === "active" ? "bg-success" : "bg-danger"
                        }`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {media.status}
                      </span>
                    </td>
                    <td>
                      <ActionBtn
                        tooltip="Delete Media"
                        color="#c62828"
                        onClick={() =>
                          handleDeleteMedia(selectedClassId, media._id)
                        }
                      >
                        <Trash size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowMediaModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default ClassMediaPage;
